import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";

const POOL_NAME = { EX: "exacta", TRI: "tri", SPR: "spr" };
const POOL_LABEL = { EX: "Exacta", TRI: "Trifecta", SPR: "Superfecta" };

const norm = (c) =>
  (c || "").toString().trim().replace(/\s+/g, "").replace(/\//g, "-").toLowerCase();

const isStraightCombo = (combo) =>
  typeof combo === "string" && (combo.startsWith("WIN") || combo.startsWith("PLC"));

// Payout match for a single ticket within a pool's payouts.
function matchPayout(ticket, payouts) {
  if (isStraightCombo(ticket.combo)) {
    const [betType, runner] = ticket.combo.split(",");
    const pn = betType === "WIN" ? "win" : "place";
    return payouts.find((p) => p.pool === pn && p.winners?.trim() === runner);
  }
  return null; // exotic handled by caller with pool name
}

function exoticPnl(code, tickets, payouts) {
  const pname = POOL_NAME[code];
  let wagered = 0;
  let returned = 0;
  let winningCombo = null;
  tickets.forEach((t) => {
    wagered += t.amount;
    const mp = payouts.find(
      (p) => p.pool === pname && norm(p.winners) === norm(t.combo)
    );
    if (mp) {
      returned += t.amount * mp.payout;
      winningCombo = t.combo;
    }
  });
  return { wagered, returned, net: returned - wagered, count: tickets.length, winningCombo };
}

function straightPnlCalc(tickets, payouts) {
  let wagered = 0;
  let returned = 0;
  let anyWin = false;
  tickets.forEach((t) => {
    wagered += t.amount;
    const mp = matchPayout(t, payouts);
    if (mp) {
      returned += t.amount * mp.payout;
      anyWin = true;
    }
  });
  return { wagered, returned, net: returned - wagered, count: tickets.length, anyWin };
}

// Compact P&L strip (Wagered / Returned / Net)
const PnlStrip = ({ pnl }) => (
  <div className="grid grid-cols-3 gap-1 text-xs p-2 border-t border-slate-700">
    <div className="bg-slate-800/50 p-1.5 rounded text-center text-slate-300">
      Wagered ${pnl.wagered.toFixed(2)}
    </div>
    <div className="bg-slate-800/50 p-1.5 rounded text-center">
      <span className={pnl.returned > 0 ? "text-green-400" : "text-slate-400"}>
        Returned ${pnl.returned.toFixed(2)}
      </span>
    </div>
    <div
      className={`p-1.5 rounded text-center ${
        pnl.net >= 0 ? "bg-green-900/20 text-green-400" : "bg-red-900/20 text-red-400"
      }`}
    >
      {pnl.net >= 0 ? "+" : ""}${pnl.net.toFixed(2)}
    </div>
  </div>
);

// Ticket table for a section. `winnerForTicket` returns payout return or 0.
// No-payout (pre-race / widget) view renders combos inline & horizontally;
// the graded payout view keeps the vertical table (Payout column + win rows).
const TicketTable = ({ tickets, hasPayouts, winnerForTicket }) => {
  if (!hasPayouts) {
    return (
      <div className="border-t border-slate-700 p-2 flex flex-wrap gap-x-2 gap-y-1.5">
        {tickets.map((t, idx) => (
          <span key={idx} className="font-mono text-sm whitespace-nowrap">
            <span className="text-slate-100">{t.combo}</span>
            <span className="text-slate-400">, </span>
            <span className="text-emerald-400">${t.amount.toFixed(2)}</span>
            {idx < tickets.length - 1 && (
              <span className="text-slate-600 ml-2 select-none">|</span>
            )}
          </span>
        ))}
      </div>
    );
  }
  return (
  <div className="max-h-[260px] overflow-y-auto border-t border-slate-700">
    <Table>
      <TableHeader>
        <TableRow className="border-slate-700 bg-slate-800">
          <TableHead className="text-slate-300 p-2">Combo</TableHead>
          <TableHead className="text-slate-300 text-right p-2">Amt</TableHead>
          {hasPayouts && (
            <TableHead className="text-slate-300 text-right p-2">Payout</TableHead>
          )}
        </TableRow>
      </TableHeader>
      <TableBody>
        {tickets.map((t, idx) => {
          const ret = winnerForTicket(t);
          const isWin = ret > 0;
          return (
            <TableRow
              key={idx}
              className={`border-slate-700 ${isWin ? "bg-green-900/20" : ""}`}
            >
              <TableCell
                className={`font-mono p-2 ${
                  isWin ? "text-green-400 font-bold" : "text-slate-100"
                }`}
              >
                {t.combo}
              </TableCell>
              <TableCell className="text-right text-emerald-400 p-2">
                ${t.amount.toFixed(2)}
              </TableCell>
              {hasPayouts && (
                <TableCell className="text-right p-2">
                  {isWin ? (
                    <span className="text-green-400 font-bold">✓ ${ret.toFixed(2)}</span>
                  ) : (
                    <span className="text-slate-500">-</span>
                  )}
                </TableCell>
              )}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </div>
  );
};

export const MultiPoolResults = ({ pools, showPnl = true }) => {
  // pools: [{ code, betAmt, data }]
  // showPnl=false (widget) never grades wagers — no payouts/returns/net.
  const hasPayouts = showPnl && pools.some((p) => (p.data?.payouts || []).length > 0);
  const racePayouts =
    pools.find((p) => (p.data?.payouts || []).length > 0)?.data?.payouts || [];

  // Per-pool exotic stats (exclude any straight tickets that rode along)
  const stats = pools.map((p) => {
    const all = p.data?.tickets || [];
    const exoticTickets = all.filter((t) => !isStraightCombo(t.combo));
    const payouts = p.data?.payouts || [];
    return {
      code: p.code,
      betAmt: p.betAmt,
      tickets: exoticTickets,
      payouts,
      pnl: exoticPnl(p.code, exoticTickets, payouts),
    };
  });

  // Straight bets (WIN/PLC) are generated once (on the first pool); collect them
  const straightTickets = pools.flatMap((p) =>
    (p.data?.tickets || []).filter((t) => isStraightCombo(t.combo))
  );
  const straight =
    straightTickets.length > 0 ? straightPnlCalc(straightTickets, racePayouts) : null;

  // Combined recap
  const combined = stats.reduce(
    (acc, s) => {
      acc.wagered += s.pnl.wagered;
      acc.returned += s.pnl.returned;
      acc.count += s.pnl.count;
      return acc;
    },
    { wagered: 0, returned: 0, count: 0 }
  );
  if (straight) {
    combined.wagered += straight.wagered;
    combined.returned += straight.returned;
    combined.count += straight.count;
  }
  combined.net = combined.returned - combined.wagered;

  // Initial view fully contracted — all sections collapsed by default
  const defaultOpen = [];

  // Global keys (same across pools) + union of runners used
  const keysUsed = pools[0]?.data?.keysUsed || [];
  const runnersUsed = [
    ...new Set(pools.flatMap((p) => p.data?.runnersUsed || [])),
  ].sort((a, b) => a - b);
  const pnBadge = (n) => {
    const num = parseInt(n, 10);
    return `pn-badge ${num >= 1 && num <= 24 ? `pn${num}` : "pn-default"}`;
  };

  return (
    <div className="space-y-3" data-testid="multi-pool-results">
      {/* Keys & Runners (global) */}
      {(keysUsed.length > 0 || runnersUsed.length > 0) && (
        <div className="space-y-1 text-sm">
          <div className="flex items-center flex-wrap gap-1">
            <span className="text-slate-400 mr-1">Keys: </span>
            {keysUsed.map((k) => (
              <span key={k} className={`${pnBadge(k)} ring-2 ring-amber-400`} data-testid={`multi-key-pn-${k}`}>
                {k}
              </span>
            ))}
          </div>
          <div className="flex items-center flex-wrap gap-1">
            <span className="text-slate-400 mr-1">Runners: </span>
            {runnersUsed.map((r) => (
              <span key={r} className={pnBadge(r)} data-testid={`multi-runner-pn-${r}`}>
                {r}
              </span>
            ))}
          </div>
        </div>
      )}
      <Accordion type="multiple" defaultValue={defaultOpen} className="space-y-2">
        {stats.map((s) => {
          const won = showPnl && !!s.pnl.winningCombo;
          const poolHasPayouts = showPnl && s.payouts.length > 0;
          return (
            <AccordionItem
              key={s.code}
              value={s.code}
              className="border border-slate-700 rounded-md overflow-hidden bg-slate-900"
              data-testid={`pool-section-${s.code}`}
            >
              <AccordionTrigger className="px-3 py-2 hover:no-underline hover:bg-slate-800">
                <div className="flex items-center gap-3 w-full text-left">
                  <span className="font-bold text-emerald-400 uppercase tracking-wide text-sm">
                    {POOL_LABEL[s.code]}
                  </span>
                  <span className="text-xs text-slate-400">
                    {s.pnl.count} tickets · ${s.pnl.wagered.toFixed(2)}
                  </span>
                  {won && (
                    <span
                      className="ml-auto mr-2 text-xs font-bold text-green-400"
                      data-testid={`pool-win-badge-${s.code}`}
                    >
                      ✓ ${s.pnl.returned.toFixed(2)}
                    </span>
                  )}
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-0 pb-0">
                <TicketTable
                  tickets={s.tickets}
                  hasPayouts={poolHasPayouts}
                  winnerForTicket={(t) => {
                    if (!showPnl) return 0;
                    const mp = s.payouts.find(
                      (p) => p.pool === POOL_NAME[s.code] && norm(p.winners) === norm(t.combo)
                    );
                    return mp ? t.amount * mp.payout : 0;
                  }}
                />
                {poolHasPayouts && <PnlStrip pnl={s.pnl} />}
              </AccordionContent>
            </AccordionItem>
          );
        })}

        {/* Straight bets (Win/Place) — their own section */}
        {straight && (
          <AccordionItem
            value="STRAIGHT"
            className="border border-slate-700 rounded-md overflow-hidden bg-slate-900"
            data-testid="pool-section-STRAIGHT"
          >
            <AccordionTrigger className="px-3 py-2 hover:no-underline hover:bg-slate-800">
              <div className="flex items-center gap-3 w-full text-left">
                <span className="font-bold text-sky-400 uppercase tracking-wide text-sm">
                  Straight (Win/Place)
                </span>
                <span className="text-xs text-slate-400">
                  {straight.count} tickets · ${straight.wagered.toFixed(2)}
                </span>
                {showPnl && straight.anyWin && (
                  <span
                    className="ml-auto mr-2 text-xs font-bold text-green-400"
                    data-testid="pool-win-badge-STRAIGHT"
                  >
                    ✓ ${straight.returned.toFixed(2)}
                  </span>
                )}
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-0 pb-0">
              <TicketTable
                tickets={straightTickets}
                hasPayouts={showPnl && racePayouts.length > 0}
                winnerForTicket={(t) => {
                  if (!showPnl) return 0;
                  const mp = matchPayout(t, racePayouts);
                  return mp ? t.amount * mp.payout : 0;
                }}
              />
              {showPnl && racePayouts.length > 0 && <PnlStrip pnl={straight} />}
            </AccordionContent>
          </AccordionItem>
        )}
      </Accordion>

      {/* Combined recap */}
      {showPnl ? (
      <div className="border-t border-slate-700 pt-2">
        <div className="text-xs text-slate-400 mb-1 font-semibold">
          Combined ({combined.count} tickets)
        </div>
        <div className="grid grid-cols-3 gap-1 text-sm">
          <div className="bg-slate-800 p-1.5 rounded text-center">
            <div className="text-[10px] text-slate-500 uppercase">Wagered</div>
            <div className="font-bold text-slate-100">${combined.wagered.toFixed(2)}</div>
          </div>
          <div className="bg-slate-800 p-1.5 rounded text-center">
            <div className="text-[10px] text-slate-500 uppercase">Returned</div>
            <div className={`font-bold ${combined.returned > 0 ? "text-green-400" : "text-slate-400"}`}>
              ${combined.returned.toFixed(2)}
            </div>
          </div>
          <div
            className={`p-1.5 rounded text-center ${
              combined.net >= 0 ? "bg-green-900/30" : "bg-red-900/30"
            }`}
          >
            <div className="text-[10px] text-slate-500 uppercase">Net</div>
            <div className={`font-bold ${combined.net >= 0 ? "text-green-400" : "text-red-400"}`}>
              {combined.net >= 0 ? "+" : ""}${combined.net.toFixed(2)}
            </div>
          </div>
        </div>
        {!hasPayouts && (
          <div className="text-[11px] text-slate-500 mt-1.5 text-center">
            Live race — P&amp;L shown after results are posted.
          </div>
        )}
      </div>
      ) : (
        <div className="border-t border-slate-700 pt-2 flex items-center justify-between text-sm">
          <span className="text-slate-400">{combined.count} tickets</span>
          <span className="font-bold text-emerald-400">Total ${combined.wagered.toFixed(2)}</span>
        </div>
      )}
    </div>
  );
};

export default MultiPoolResults;
